/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * 
 * BLOCO 3 — 30 DIAS GRATUITOS SEM QUADRAS + PLANO OPERAÇÃO (OPERATION_ONLY)
 * Entitlement Service Autorizativo Central
 * 
 * Regra:
 * O proprietário que ainda não possui nenhuma quadra canônica tem 30 DIAS GRATUITOS
 * para utilizar os módulos operacionais (Vendas, PDV, Comandas, Estoque, Financeiro).
 * Após 30 dias, estes módulos são bloqueados até contratação do PLANO OPERAÇÃO (OPERATION_ONLY).
 * 
 * O bloqueio NUNCA afeta:
 * - Login do usuário
 * - Módulo de Torneios
 * - Dados cadastrados (Arena, vendas, configurações)
 */

import { auth, db } from "../../utils/firebase";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import {
  DEFAULT_COMMERCIAL_PLANS,
  DEFAULT_OPERATION_ONLY_MONTHLY_FEE,
  getSystemSettings,
} from "../../utils/commissionConfig";
import { OFFICIAL_ADMIN_EMAILS, CourtItem } from "../../types/userProfile";
import {
  isArenaApproved,
  isArenaOperationallyActive,
  isCourtApproved,
  isCourtOperationallyActive,
} from "../arenaVerification/arenaVisibilityService";

export const TRIAL_DURATION_DAYS = 30;
export const TRIAL_DURATION_MS = TRIAL_DURATION_DAYS * 24 * 60 * 60 * 1000; // 30 dias em milissegundos

export interface EntitlementContext {
  arena?: {
    id?: string;
    arenaId?: string;
    createdAt?: number | string;
    trialStartAt?: number;
    trialEndAt?: number;
    trialStatus?: "TRIAL_ACTIVE" | "TRIAL_EXPIRED" | "OPERATION_ACTIVE";
    commercialStatus?: "TRIAL_ACTIVE" | "TRIAL_EXPIRED" | "OPERATION_ACTIVE";
    financialModel?: string;
    commercialPlanId?: string;
    isOperationOnly?: boolean;
    verificationStatus?: string;
    status?: string;
    [key: string]: any;
  } | null;
  userProfile?: {
    id?: string;
    accountType?: string;
    isSuperAdmin?: boolean;
    role?: string;
    email?: string;
    createdAt?: number | string;
    trialStartAt?: number;
    trialEndAt?: number;
    trialStatus?: "TRIAL_ACTIVE" | "TRIAL_EXPIRED" | "OPERATION_ACTIVE";
    commercialStatus?: "TRIAL_ACTIVE" | "TRIAL_EXPIRED" | "OPERATION_ACTIVE";
    isOperationOnly?: boolean;
    subscriptionStatus?: string;
    [key: string]: any;
  } | null;
  courtsCount?: number;
  courts?: CourtItem[] | null;
  now?: number; // Injeção de timestamp para testes determinísticos
}

export type CommercialEntitlementStatusType =
  | "TRIAL_ACTIVE"
  | "TRIAL_EXPIRED"
  | "OPERATION_ACTIVE"
  | "REGULAR_ACTIVE"
  | "PDV_ACTIVE"
  | "PDV_EXPIRED"
  | "BLOCKED"
  | "SUPER_ADMIN";

export type EntitlementModuleKey =
  | "operational"
  | "operacao"
  | "operacional"
  | "vendas"
  | "sales"
  | "financeiro"
  | "finance"
  | "pdv"
  | "comandas"
  | "estoque"
  | "stock"
  | "relatorios"
  | "reports"
  | "torneios"
  | "tournaments"
  | "configuracao_arena"
  | "arena_config"
  | "quadras_config"
  | "courts_config"
  | "login"
  | "perfil"
  | "profile";

export interface CommercialEntitlementResult {
  status: CommercialEntitlementStatusType;
  canAccessOperational: boolean;
  canAccessSales: boolean;
  canAccessFinance: boolean;
  canAccessPDV: boolean;
  canAccessStock: boolean;
  canAccessReports: boolean;
  canAccessTournament: boolean;
  canAccessArenaConfig: boolean;
  canAccessCourtsConfig: boolean;
  canAccessLogin: boolean;
  canAccessProfile: boolean;
  isWithoutCourts: boolean;
  isOperationOnly: boolean;
  isBlocked: boolean;
  hasOperationProduct?: boolean;
  hasReservationModel?: boolean;
  productType?: "OPERATION_ONLY" | "COURT_MODEL" | "TRIAL" | "REGULAR_RESERVATION_PRODUCT" | "PDV_VENDAS_LOCAIS";
  reservationModel?: "SPLIT" | "FIXED_MONTHLY" | "HYBRID" | null;
  commercialPlanId?: string;
  commercialPlanName?: string;
  trialStartAt?: number;
  trialEndAt?: number;
  daysRemaining?: number;
  message?: string;
  reason?: string;
  canAccess: (module: EntitlementModuleKey | string) => boolean;
}

/**
 * Função estável reutilizável para checagem por módulo em CommercialEntitlementResult.
 */
function moduleChecker(this: CommercialEntitlementResult, module: EntitlementModuleKey | string): boolean {
  const key = String(module).toLowerCase().trim();
  switch (key) {
    case "operational":
    case "operacao":
    case "operacional":
      return this.canAccessOperational;
    case "vendas":
    case "sales":
      return this.canAccessSales;
    case "financeiro":
    case "finance":
      return this.canAccessFinance;
    case "pdv":
    case "comandas":
      return this.canAccessPDV;
    case "estoque":
    case "stock":
      return this.canAccessStock;
    case "relatorios":
    case "reports":
      return this.canAccessReports;
    case "torneios":
    case "torneio":
    case "tournaments":
    case "tournament":
      return this.canAccessTournament;
    case "configuracao_arena":
    case "arena_config":
    case "arena":
      return this.canAccessArenaConfig;
    case "quadras_config":
    case "courts_config":
    case "quadras":
    case "courts":
      return this.canAccessCourtsConfig;
    case "login":
    case "auth":
      return this.canAccessLogin;
    case "perfil":
    case "profile":
      return this.canAccessProfile;
    default:
      return this.canAccessOperational;
  }
}

/**
 * Converte qualquer formato de data (number timestamp ou ISO string) para timestamp numérico seguro.
 */
export function normalizeDateToTimestamp(rawDate?: number | string | null): number | null {
  if (rawDate === undefined || rawDate === null || rawDate === "") return null;
  if (typeof rawDate === "number" && !isNaN(rawDate)) return rawDate;
  if (typeof rawDate === "string") {
    const num = Number(rawDate);
    if (!isNaN(num) && num > 0) return num;
    const parsed = new Date(rawDate).getTime();
    if (!isNaN(parsed) && parsed > 0) return parsed;
  }
  return null;
}

export class EntitlementService {
  /**
   * Obtém o preço oficial do Plano Operação a partir das fontes canônicas de configuração comercial.
   * NUNCA inventa uma segunda fonte de preço.
   */
  static getOperationOnlyPrice(): number {
    try {
      const plan = DEFAULT_COMMERCIAL_PLANS.find(
        (p) => p.id === "plan_operation_only" || p.financialModel === "OPERATION_ONLY"
      );
      if (plan && typeof plan.monthlyFee === "number" && plan.monthlyFee > 0) {
        return plan.monthlyFee;
      }
      const settings = getSystemSettings();
      if (typeof settings.operationOnlyMonthlyFee === "number" && settings.operationOnlyMonthlyFee > 0) {
        return settings.operationOnlyMonthlyFee;
      }
    } catch {}
    return DEFAULT_OPERATION_ONLY_MONTHLY_FEE;
  }

  /**
   * Resolve as datas canônicas de início e término do período de trial de 30 dias.
   * Garantia: trialStartAt + 30 dias = trialEndAt (determinístico e persistente).
   */
  static getEffectiveTrialDates(
    arena?: EntitlementContext["arena"],
    userProfile?: EntitlementContext["userProfile"],
    fallbackNow: number = Date.now()
  ): { trialStartAt: number; trialEndAt: number } {
    // 1. Tenta pegar a data de início explícita da Arena
    let startTimestamp = normalizeDateToTimestamp(arena?.trialStartAt);

    // 2. Se não existir, tenta o createdAt da Arena
    if (!startTimestamp) {
      startTimestamp = normalizeDateToTimestamp(arena?.createdAt);
    }

    // 3. Se não existir, tenta o trialStartAt do perfil do usuário
    if (!startTimestamp) {
      startTimestamp = normalizeDateToTimestamp(userProfile?.trialStartAt);
    }

    // 4. Se não existir, tenta o createdAt do perfil do usuário
    if (!startTimestamp) {
      startTimestamp = normalizeDateToTimestamp(userProfile?.createdAt);
    }

    // 5. Fallback final: timestamp do momento da avaliação
    if (!startTimestamp) {
      startTimestamp = fallbackNow;
    }

    // Data de término: se a arena já tem trialEndAt persistido, respeita; senão, calcula start + 30 dias
    let endTimestamp = normalizeDateToTimestamp(arena?.trialEndAt);
    if (!endTimestamp) {
      endTimestamp = startTimestamp + TRIAL_DURATION_MS;
    }

    return {
      trialStartAt: startTimestamp,
      trialEndAt: endTimestamp,
    };
  }

  /**
   * Verifica se o contexto possui o Plano Operação (OPERATION_ONLY) contratado/ativo.
   */
  static isOperationOnlyActive(
    arena?: EntitlementContext["arena"],
    userProfile?: EntitlementContext["userProfile"]
  ): boolean {
    if (!arena && !userProfile) return false;

    if (
      arena?.financialModel === "OPERATION_ONLY" ||
      arena?.commercialPlanId === "plan_operation_only" ||
      arena?.commercialPlanId === "operation_only" ||
      arena?.isOperationOnly === true ||
      arena?.commercialStatus === "OPERATION_ACTIVE" ||
      arena?.trialStatus === "OPERATION_ACTIVE"
    ) {
      return true;
    }

    if (
      userProfile?.isOperationOnly === true ||
      userProfile?.arenaFinancialModel === "OPERATION_ONLY" ||
      userProfile?.commercialStatus === "OPERATION_ACTIVE" ||
      userProfile?.trialStatus === "OPERATION_ACTIVE"
    ) {
      return true;
    }

    return false;
  }

  /**
   * Verifica se a Arena e pelo menos uma Quadra atendem a TODOS os critérios de elegibilidade operacional:
   * 1. Arena Aprovada (isArenaApproved)
   * 2. Arena Operacionalmente Ativa (isArenaOperationallyActive)
   * 3. Pelo menos 1 Quadra Aprovada (isCourtApproved)
   * 4. Essa Quadra Operacionalmente Ativa (isCourtOperationallyActive)
   */
  static isOperationallyEligible(
    arena?: EntitlementContext["arena"],
    userProfile?: EntitlementContext["userProfile"],
    courts?: CourtItem[] | null,
    courtsCount?: number
  ): boolean {
    const arenaObj = arena || userProfile;
    if (!arenaObj) return false;

    // 1. Arena não pode ter status de aprovação pendente, em análise, rejeitada ou bloqueada
    const vStatus = (
      arenaObj.verificationStatus ||
      arenaObj.resolvedVerificationStatus ||
      ""
    ).toString().toUpperCase().trim();

    const isExplicitlyPendingOrBlocked = [
      "PENDING_APPROVAL",
      "PENDING",
      "PENDENTE",
      "UNDER_REVIEW",
      "EM_ANALISE",
      "EM_ANÁLISE",
      "REJECTED",
      "REJEITADA",
      "BLOCKED",
      "BLOQUEADA",
      "SUSPENDED",
      "SUSPENSA",
    ].includes(vStatus);

    if (isExplicitlyPendingOrBlocked) {
      return false;
    }

    // 2. Arena deve estar operacionalmente ativa
    if (!isArenaOperationallyActive(arenaObj)) {
      return false;
    }

    // Regra 1: Se uma lista explícita de quadras for fornecida e não estiver vazia:
    const courtsList = courts || (arena as any)?.courts || (arena as any)?.allCourts;
    if (Array.isArray(courtsList) && courtsList.length > 0) {
      const hasApprovedCourt = courtsList.some((c) => isCourtApproved(c) && isCourtOperationallyActive(c));
      if (!hasApprovedCourt) {
        return false;
      }
      return true;
    }

    // Regra 2: Se a lista de quadras não for fornecida (ou estiver vazia no contexto):
    // A) Se courtsCount >= 1, activeCourtCount > 0, ou hasApprovedCourts === true:
    const effectiveCourtsCount =
      typeof courtsCount === "number"
        ? courtsCount
        : ((arena as any)?.activeCourtCount ?? (userProfile as any)?.activeCourtCount ?? 0);

    if (effectiveCourtsCount >= 1) {
      return true;
    }

    if ((arena as any)?.hasApprovedCourts === true || (userProfile as any)?.hasApprovedCourts === true) {
      return true;
    }

    // B) Regra B (Prompt 4): Se a Arena for formalmente Aprovada E Operacionalmente Ativa:
    // Mesmo que o contexto atual de quadras venha zerado/filtrado/vazio (courtsCount === 0),
    // a Arena já ativa e homologada é operacionalmente elegível e NUNCA cai em TRIAL_EXPIRED!
    if (isArenaApproved(arenaObj) && isArenaOperationallyActive(arenaObj)) {
      return true;
    }

    return false;
  }

  /**
   * Verifica se a arena ou perfil do usuário está associado ao Plano Inicial PDV / Vendas Locais.
   * Regra BLOCO 12A / 12B: Todo novo Dono de Arena entra com o plano PDV / VENDAS LOCAIS.
   * Quando a Arena for aprovada/operacional E houver ao menos 1 Quadra aprovada/operacional,
   * o plano PDV é automaticamente substituído pelo modelo comercial da Quadra (SPLIT/FIXED_MONTHLY/HYBRID).
   */
  static isPDVPlan(
    arena?: EntitlementContext["arena"],
    userProfile?: EntitlementContext["userProfile"],
    courtsCount?: number,
    courts?: CourtItem[] | null
  ): boolean {
    if (!arena && !userProfile) return false;

    // Se a Arena for operacionalmente elegível (Arena aprovada/ativa + Quadra aprovada/ativa),
    // sai do plano PDV e transiciona para o modelo comercial de quadra
    if (this.isOperationallyEligible(arena, userProfile, courts, courtsCount)) {
      return false;
    }

    // Se estiver explicitamente contratado no Plano Operação (OPERATION_ONLY)
    if (this.isOperationOnlyActive(arena, userProfile)) {
      return false;
    }

    // Se tiver marcações de PDV / Vendas Locais
    if (
      arena?.commercialPlanId === "plan_pdv_vendas_locais" ||
      arena?.financialModel === "PDV_VENDAS_LOCAIS" ||
      arena?.isPDVPlan === true ||
      arena?.commercialStatus === "PDV_ACTIVE" ||
      arena?.commercialStatus === "PDV_EXPIRED" ||
      arena?.trialStatus === "PDV_ACTIVE" ||
      arena?.trialStatus === "PDV_EXPIRED"
    ) {
      return true;
    }

    if (
      userProfile?.commercialPlanId === "plan_pdv_vendas_locais" ||
      userProfile?.financialModel === "PDV_VENDAS_LOCAIS" ||
      userProfile?.arenaFinancialModel === "PDV_VENDAS_LOCAIS" ||
      userProfile?.financialConfig?.financialModel === "PDV_VENDAS_LOCAIS" ||
      userProfile?.financialConfig?.commercialPlanId === "plan_pdv_vendas_locais" ||
      userProfile?.isPDVPlan === true ||
      userProfile?.commercialStatus === "PDV_ACTIVE" ||
      userProfile?.commercialStatus === "PDV_EXPIRED" ||
      userProfile?.trialStatus === "PDV_ACTIVE" ||
      userProfile?.trialStatus === "PDV_EXPIRED"
    ) {
      return true;
    }

    // Dono de Arena ou perfil com Arena ainda NÃO operacionalmente elegível -> Permanece no fluxo comercial PDV
    const isArenaOwner =
      userProfile?.accountType === "dono_arena" ||
      userProfile?.role === "arena_owner";

    if (isArenaOwner) {
      return true;
    }

    return false;
  }

  /**
   * Identifica se a arena ou conta do usuário possui suspensão ou bloqueio administrativo ativo.
   */
  static isBlockedStatus(
    arena?: EntitlementContext["arena"],
    userProfile?: EntitlementContext["userProfile"]
  ): boolean {
    if (
      arena?.status === "blocked" ||
      arena?.status === "suspended" ||
      arena?.commercialStatus === "BLOCKED" ||
      arena?.commercialStatus === "SUSPENDED" ||
      arena?.isBlocked === true ||
      arena?.verificationStatus === "blocked" ||
      arena?.verificationStatus === "suspended" ||
      arena?.subscriptionStatus === "SUSPENDED" ||
      arena?.subscriptionStatus === "EXPIRED"
    ) {
      return true;
    }
    if (
      userProfile?.status === "blocked" ||
      userProfile?.status === "suspended" ||
      userProfile?.commercialStatus === "BLOCKED" ||
      userProfile?.commercialStatus === "SUSPENDED" ||
      userProfile?.isBlocked === true ||
      userProfile?.subscriptionStatus === "SUSPENDED" ||
      userProfile?.subscriptionStatus === "EXPIRED"
    ) {
      return true;
    }
    return false;
  }

  /**
   * Avalia de forma determinística os privilégios de acesso comercial do proprietário/arena.
   */
  static getCommercialEntitlementStatus(context: EntitlementContext): CommercialEntitlementResult {
    const { arena, userProfile, courtsCount = 0, now = Date.now() } = context;

    // 1. Super Admin possui bypass completo
    const isSuperAdmin =
      userProfile?.isSuperAdmin === true ||
      userProfile?.role === "admin" ||
      (userProfile?.email && OFFICIAL_ADMIN_EMAILS.includes(userProfile.email.toLowerCase().trim()));

    if (isSuperAdmin) {
      const flags = {
        canAccessOperational: true,
        canAccessSales: true,
        canAccessFinance: true,
        canAccessPDV: true,
        canAccessStock: true,
        canAccessReports: true,
        canAccessTournament: true,
        canAccessArenaConfig: true,
        canAccessCourtsConfig: true,
        canAccessLogin: true,
        canAccessProfile: true,
      };

      const resolvedResModel = (arena?.financialModel === "SPLIT" || arena?.financialModel === "FIXED_MONTHLY" || arena?.financialModel === "HYBRID")
        ? arena.financialModel
        : "SPLIT";

      return {
        status: "SUPER_ADMIN",
        ...flags,
        isWithoutCourts: courtsCount === 0,
        isOperationOnly: false,
        isBlocked: false,
        hasOperationProduct: true,
        hasReservationModel: true,
        productType: "REGULAR_RESERVATION_PRODUCT",
        reservationModel: resolvedResModel,
        reason: "Acesso administrativo irrestrito concedido a Super Admin.",
        canAccess: moduleChecker,
      };
    }

    // 2. Arena ou Conta explicitamente Bloqueada/Suspensa administrativamente
    if (this.isBlockedStatus(arena, userProfile)) {
      const flags = {
        canAccessOperational: false,
        canAccessSales: false,
        canAccessFinance: false,
        canAccessPDV: false,
        canAccessStock: false,
        canAccessReports: false,
        canAccessTournament: false,
        canAccessArenaConfig: false,
        canAccessCourtsConfig: false,
        canAccessLogin: true, // Login permanece ativo para autenticação/auditoria
        canAccessProfile: true, // Perfil permanece ativo para contato/regularização
      };

      return {
        status: "BLOCKED",
        ...flags,
        isWithoutCourts: courtsCount === 0,
        isOperationOnly: false,
        isBlocked: true,
        hasOperationProduct: false,
        hasReservationModel: false,
        productType: "OPERATION_ONLY",
        reservationModel: null,
        message: "Acesso aos módulos operacionais e de arena bloqueado ou suspenso administrativamente.",
        reason: "Arena ou conta com restrição/bloqueio administrativo ativo.",
        canAccess: moduleChecker,
      };
    }

    // 3. BLOCO 12A / 12B: PLANO INICIAL PDV / VENDAS LOCAIS (30 DIAS)
    if (this.isPDVPlan(arena, userProfile, courtsCount, context.courts)) {
      const { trialStartAt, trialEndAt } = this.getEffectiveTrialDates(arena, userProfile, now);
      const isExpired = now >= trialEndAt;
      const msRemaining = Math.max(0, trialEndAt - now);
      const daysRemaining = isExpired ? 0 : Math.ceil(msRemaining / (24 * 60 * 60 * 1000));
      const isPlanActive = !isExpired;

      const flags = {
        canAccessOperational: isPlanActive,
        canAccessSales: isPlanActive,
        canAccessFinance: isPlanActive,
        canAccessPDV: isPlanActive,
        canAccessStock: isPlanActive,
        canAccessReports: isPlanActive,
        canAccessTournament: false,   // REGRA MANDATÓRIA BLOCO 12A: Torneios NÃO liberados no Plano PDV
        canAccessArenaConfig: true,   // REGRA MANDATÓRIA BLOCO 12A: Cadastro e gestão de Arena NÃO são bloqueados
        canAccessCourtsConfig: true,  // REGRA MANDATÓRIA BLOCO 12A: Cadastro e gestão de Quadras NÃO são bloqueados
        canAccessLogin: true,
        canAccessProfile: true,
      };

      const status: CommercialEntitlementStatusType = isPlanActive ? "PDV_ACTIVE" : "PDV_EXPIRED";

      return {
        status,
        ...flags,
        isWithoutCourts: courtsCount === 0,
        isOperationOnly: false,
        isBlocked: !isPlanActive,
        hasOperationProduct: isPlanActive,
        hasReservationModel: false,
        productType: "PDV_VENDAS_LOCAIS",
        reservationModel: null,
        commercialPlanId: "plan_pdv_vendas_locais",
        commercialPlanName: "PDV / VENDAS LOCAIS",
        trialStartAt,
        trialEndAt,
        daysRemaining,
        message: isPlanActive
          ? `Plano Inicial PDV / Vendas Locais Ativo (${daysRemaining} ${daysRemaining === 1 ? "dia restante" : "dias restantes"})`
          : "Seu plano inicial PDV / Vendas Locais de 30 dias expirou.",
        reason: isPlanActive
          ? "Dono de Arena sob o Plano Inicial PDV / Vendas Locais (30 dias)."
          : "Plano Inicial PDV / Vendas Locais expirado.",
        canAccess: moduleChecker,
      };
    }

    // 4. Se for Plano Operação (OPERATION_ONLY) ativo
    if (this.isOperationOnlyActive(arena, userProfile)) {
      const flags = {
        canAccessOperational: true,
        canAccessSales: true,
        canAccessFinance: true,
        canAccessPDV: true,
        canAccessStock: true,
        canAccessReports: true,
        canAccessTournament: true,
        canAccessArenaConfig: true,
        canAccessCourtsConfig: true,
        canAccessLogin: true,
        canAccessProfile: true,
      };

      return {
        status: "OPERATION_ACTIVE",
        ...flags,
        isWithoutCourts: courtsCount === 0,
        isOperationOnly: true,
        isBlocked: false,
        hasOperationProduct: true,
        hasReservationModel: false,
        productType: "OPERATION_ONLY",
        reservationModel: null,
        message: "Plano Operação Ativo (Produto de Operação Comercial — sem modelo de reservas ativo)",
        reason: "Proprietário contratou o Plano Operação.",
        canAccess: moduleChecker,
      };
    }

    // 5. Se a Arena e ao menos uma Quadra forem operacionalmente elegíveis (Aprovadas e Ativas)
    if (this.isOperationallyEligible(arena, userProfile, context.courts, courtsCount)) {
      const flags = {
        canAccessOperational: true,
        canAccessSales: true,
        canAccessFinance: true,
        canAccessPDV: true,
        canAccessStock: true,
        canAccessReports: true,
        canAccessTournament: true,
        canAccessArenaConfig: true,
        canAccessCourtsConfig: true,
        canAccessLogin: true,
        canAccessProfile: true,
      };

      const rawModel =
        arena?.financialModel ||
        arena?.financialConfig?.financialModel ||
        arena?.financialConfig?.model ||
        arena?.arenaFinancialModel;

      const isOpOnly =
        rawModel === "OPERATION_ONLY" ||
        arena?.isOperationOnly === true ||
        userProfile?.isOperationOnly === true ||
        arena?.planType === "OPERATION_ONLY";

      // Toda arena canônica com quadras que NÃO é OPERATION_ONLY tem modelo comercial de reservas ativo
      // O padrão canônico da plataforma é SPLIT caso não esteja configurado explicitamente
      const hasRegularReservationModel = !isOpOnly;

      const currentResModel: "SPLIT" | "FIXED_MONTHLY" | "HYBRID" | null =
        hasRegularReservationModel
          ? (rawModel === "FIXED_MONTHLY" || rawModel === "HYBRID" ? rawModel : "SPLIT")
          : null;

      return {
        status: "REGULAR_ACTIVE",
        ...flags,
        isWithoutCourts: false,
        isOperationOnly: isOpOnly,
        isBlocked: false,
        hasOperationProduct: true,
        hasReservationModel: hasRegularReservationModel,
        productType: hasRegularReservationModel ? "COURT_MODEL" : "OPERATION_ONLY",
        reservationModel: currentResModel,
        commercialPlanId: arena?.commercialPlanId || "split_1_open",
        commercialPlanName: currentResModel === "SPLIT" ? "SPLIT (Transacional)" : (currentResModel || "SPLIT"),
        reason: isOpOnly
          ? "Arena com quadras cadastradas operando sob Plano Operação (módulos operacionais sem modelo de reservas)."
          : "Arena canônica com quadras esportivas cadastradas.",
        canAccess: moduleChecker,
      };
    }

    // 5. ARENA CANÔNICA SEM QUADRAS (courtsCount === 0)
    const { trialStartAt, trialEndAt } = this.getEffectiveTrialDates(arena, userProfile, now);
    const isExpired = now >= trialEndAt;
    const msRemaining = Math.max(0, trialEndAt - now);
    const daysRemaining = isExpired ? 0 : Math.ceil(msRemaining / (24 * 60 * 60 * 1000));

    if (!isExpired) {
      const flags = {
        canAccessOperational: true,
        canAccessSales: true,
        canAccessFinance: true,
        canAccessPDV: true,
        canAccessStock: true,
        canAccessReports: true,
        canAccessTournament: true,
        canAccessArenaConfig: true,
        canAccessCourtsConfig: true,
        canAccessLogin: true,
        canAccessProfile: true,
      };

      return {
        status: "TRIAL_ACTIVE",
        ...flags,
        isWithoutCourts: true,
        isOperationOnly: false,
        isBlocked: false,
        hasOperationProduct: true,
        hasReservationModel: false,
        productType: "TRIAL",
        reservationModel: null,
        trialStartAt,
        trialEndAt,
        daysRemaining,
        message: `Período gratuito: ${daysRemaining} ${daysRemaining === 1 ? "dia restante" : "dias restantes"}`,
        reason: `Arena sem quadras dentro do período gratuito de 30 dias (término em ${new Date(trialEndAt).toLocaleDateString("pt-BR")}).`,
        canAccess: moduleChecker,
      };
    }

    // Trial expirado
    const flags = {
      canAccessOperational: false,
      canAccessSales: false,
      canAccessFinance: false,
      canAccessPDV: false,
      canAccessStock: false,
      canAccessReports: false,
      canAccessTournament: true,   // Regra obrigatória: Torneios NUNCA são bloqueados
      canAccessArenaConfig: true, // Regra obrigatória: Configuração de Arena NUNCA é bloqueada
      canAccessCourtsConfig: true,// Regra obrigatória: Cadastro de Quadras NUNCA é bloqueado
      canAccessLogin: true,        // Regra obrigatória: Login NUNCA é bloqueado
      canAccessProfile: true,      // Regra obrigatória: Perfil NUNCA é bloqueado
    };

    return {
      status: "TRIAL_EXPIRED",
      ...flags,
      isWithoutCourts: true,
      isOperationOnly: false,
      isBlocked: true, // Bloqueio operacional
      hasOperationProduct: false,
      hasReservationModel: false,
      productType: "TRIAL",
      reservationModel: null,
      trialStartAt,
      trialEndAt,
      daysRemaining: 0,
      message: "Seu período gratuito de 30 dias terminou. Assine o Plano Operação para continuar utilizando os módulos operacionais.",
      reason: "Período gratuito de 30 dias expirado para Arena sem quadras.",
      canAccess: moduleChecker,
    };
  }

  /**
   * Autorização canônica por módulo.
   */
  static canAccess(module: EntitlementModuleKey | string, context: EntitlementContext): boolean {
    return this.getCommercialEntitlementStatus(context).canAccess(module);
  }

  /**
   * Atalhos de verificação booleana rápida
   */
  static hasOperationProduct(context: EntitlementContext): boolean {
    return Boolean(this.getCommercialEntitlementStatus(context).hasOperationProduct);
  }

  static hasReservationModel(context: EntitlementContext): boolean {
    return Boolean(this.getCommercialEntitlementStatus(context).hasReservationModel);
  }

  static getProductType(context: EntitlementContext) {
    return this.getCommercialEntitlementStatus(context).productType;
  }

  static getReservationModel(context: EntitlementContext) {
    return this.getCommercialEntitlementStatus(context).reservationModel;
  }

  static canAccessOperationalModules(context: EntitlementContext): boolean {
    return this.getCommercialEntitlementStatus(context).canAccessOperational;
  }

  static canAccessSales(context: EntitlementContext): boolean {
    return this.getCommercialEntitlementStatus(context).canAccessSales;
  }

  static canAccessFinance(context: EntitlementContext): boolean {
    return this.getCommercialEntitlementStatus(context).canAccessFinance;
  }

  static canAccessPDV(context: EntitlementContext): boolean {
    return this.getCommercialEntitlementStatus(context).canAccessPDV;
  }

  static canAccessStock(context: EntitlementContext): boolean {
    return this.getCommercialEntitlementStatus(context).canAccessStock;
  }

  static canAccessReports(context: EntitlementContext): boolean {
    return this.getCommercialEntitlementStatus(context).canAccessReports;
  }

  static canAccessTournament(context: EntitlementContext): boolean {
    return this.getCommercialEntitlementStatus(context).canAccessTournament;
  }

  static canAccessArenaConfig(context: EntitlementContext): boolean {
    return this.getCommercialEntitlementStatus(context).canAccessArenaConfig;
  }

  static canAccessCourtsConfig(context: EntitlementContext): boolean {
    return this.getCommercialEntitlementStatus(context).canAccessCourtsConfig;
  }

  static canAccessLogin(context: EntitlementContext): boolean {
    return this.getCommercialEntitlementStatus(context).canAccessLogin;
  }

  static canAccessProfile(context: EntitlementContext): boolean {
    return this.getCommercialEntitlementStatus(context).canAccessProfile;
  }

  /**
   * Garante a persistência dos campos de trial no documento da Arena e no cache local
   * caso a Arena canônica sem quadras ainda não possua os campos persistidos.
   */
  static async ensureArenaTrialInitialized(
    arenaId: string,
    arenaData?: any,
    userProfile?: any
  ): Promise<{ trialStartAt: number; trialEndAt: number; trialStatus: string }> {
    const { trialStartAt, trialEndAt } = this.getEffectiveTrialDates(arenaData, userProfile);
    const now = Date.now();
    const trialStatus = now >= trialEndAt ? "TRIAL_EXPIRED" : "TRIAL_ACTIVE";

    // Se já tinha os campos persistidos, não reescreve
    if (arenaData?.trialStartAt && arenaData?.trialEndAt) {
      return {
        trialStartAt: Number(arenaData.trialStartAt),
        trialEndAt: Number(arenaData.trialEndAt),
        trialStatus: arenaData.trialStatus || trialStatus,
      };
    }

    try {
      if (db && arenaId) {
        const arenaRef = doc(db, "arenas", arenaId);
        await setDoc(
          arenaRef,
          {
            trialStartAt,
            trialEndAt,
            trialStatus,
            updatedAt: now,
          },
          { merge: true }
        );
      }
    } catch (err) {
      console.warn("Aviso ao persistir campos de trial da arena no Firestore:", err);
    }

    // Atualiza cache local de arenas
    try {
      const saved = localStorage.getItem("app_arenas_canonical_v1");
      if (saved) {
        const list = JSON.parse(saved);
        if (Array.isArray(list)) {
          const idx = list.findIndex((a: any) => a.id === arenaId || a.arenaId === arenaId);
          if (idx >= 0) {
            list[idx] = {
              ...list[idx],
              trialStartAt,
              trialEndAt,
              trialStatus,
            };
            localStorage.setItem("app_arenas_canonical_v1", JSON.stringify(list));
          }
        }
      }
    } catch {}

    return { trialStartAt, trialEndAt, trialStatus };
  }

  /**
   * Ativa o Plano Operação (OPERATION_ONLY) para a Arena/Proprietário.
   * Não altera verificationStatus, não publica arena e não altera modelo de split de reservas.
   */
  static async activateOperationOnlyPlan(
    arenaId: string,
    ownerUid: string
  ): Promise<{ success: boolean; message: string }> {
    const now = Date.now();
    const price = this.getOperationOnlyPrice();

    try {
      if (db && arenaId) {
        const arenaRef = doc(db, "arenas", arenaId);
        await setDoc(
          arenaRef,
          {
            financialModel: "OPERATION_ONLY",
            commercialPlanId: "plan_operation_only",
            isOperationOnly: true,
            commercialStatus: "OPERATION_ACTIVE",
            trialStatus: "OPERATION_ACTIVE",
            updatedAt: now,
          },
          { merge: true }
        );

        // Também salva na configuração comercial da arena
        const configRef = doc(db, "arena_financial_configs", arenaId);
        await setDoc(
          configRef,
          {
            arenaId,
            financialModel: "OPERATION_ONLY",
            productType: "OPERATION_ONLY",
            commercialPlanId: "plan_operation_only",
            monthlyFee: price,
            commissionRate: 0,
            isOperationOnly: true,
            commercialStatus: "OPERATION_ACTIVE",
            trialStatus: "OPERATION_ACTIVE",
            updatedAt: new Date(now).toISOString(),
          },
          { merge: true }
        );
      }

      if (db && ownerUid && auth?.currentUser?.uid === ownerUid) {
        const userRef = doc(db, "users", ownerUid);
        await setDoc(
          userRef,
          {
            isOperationOnly: true,
            commercialStatus: "OPERATION_ACTIVE",
            trialStatus: "OPERATION_ACTIVE",
            updatedAt: now,
          },
          { merge: true }
        );
      }

      // Atualiza cache local
      try {
        const saved = localStorage.getItem("app_arenas_canonical_v1");
        if (saved) {
          const list = JSON.parse(saved);
          if (Array.isArray(list)) {
            const idx = list.findIndex((a: any) => a.id === arenaId || a.arenaId === arenaId);
            if (idx >= 0) {
              list[idx] = {
                ...list[idx],
                financialModel: "OPERATION_ONLY",
                commercialPlanId: "plan_operation_only",
                isOperationOnly: true,
                commercialStatus: "OPERATION_ACTIVE",
                trialStatus: "OPERATION_ACTIVE",
              };
              localStorage.setItem("app_arenas_canonical_v1", JSON.stringify(list));
            }
          }
        }
      } catch {}

      return {
        success: true,
        message: "Plano Operação ativado com sucesso!",
      };
    } catch (err: any) {
      console.error("Erro ao ativar Plano Operação:", err);
      return {
        success: false,
        message: err?.message || "Falha ao ativar o Plano Operação.",
      };
    }
  }
}
